import { call, put, all, takeEvery, takeLatest, race, take, delay } from 'redux-saga/effects'
import { SagaReturnType } from 'redux-saga/effects'

import { showToasts } from '../../helpers/toasts'
import {
  CREATE_ASSET,
  createAssetSuccess,
  createAssetFailure,
  CREATE_ASSETS_BATCH,
  createAssetsBatchSuccess,
  createAssetsBatchFailure,
  CREATE_ASSET_GROUP,
  createAssetGroupSuccess,
  createAssetGroupFailure,
  GET_ASSET_GROUPS,
  CLEAR_ASSET_GROUPS,
  getAssetGroupsSuccess,
  getAssetGroupsFailure,
  GET_ASSET_GROUP,
  getAssetGroupSuccess,
  getAssetGroupFailure,
  UPDATE_ASSET_GROUP,
  updateAssetGroupSuccess,
  updateAssetGroupFailure,
  CREATE_ASSET_GROUP_SIGNAL,
  createAssetGroupSignalSuccess,
  createAssetGroupSignalFailure,
  GET_ASSETS,
  CLEAR_ASSETS,
  getAssetsSuccess,
  getAssetsFailure,
  APPROVE_INTERNAL_ASSET_GROUP,
  approveInternalAssetGroupSuccess,
  approveInternalAssetGroupFailure,
  DISAPPROVE_INTERNAL_ASSET_GROUP,
  disapproveInternalAssetGroupSuccess,
  disapproveInternalAssetGroupFailure
} from '../actions/assets'
import { fileUploadToPlatformFailure, fileUploadToPlatformSuccess } from '../actions/files'
import {
  CreateAssetPayload,
  GetAssetsPayload,
  CreateAssetsBatchPayload,
  CreateAssetGroupPayload,
  GetAssetGroupsPayload,
  GetAssetGroupPayload,
  UpdateAssetGroupPayload,
  ApproveInternalAssetGroupPayload,
  DisapproveInternalAssetGroupPayload,
  CreateAssetGroupSignalPayload
} from '../types/assets'

import {
  createAssetService,
  createAssetsBatchService,
  createAssetGroupService,
  getAssetGroupsService,
  getAssetGroupService,
  updateAssetGroupService,
  createAssetGroupSignalService,
  getAssetsService,
  disapproveInternalAssetGroupService,
  approveInternalAssetGroupService
} from '../services/assets'

import { TOAST_TYPE } from '../../constants/other'
import { GOOGLE_PLATFORM } from '../../constants/selectLists/platformList'

function* assetsWatcher() {
  yield all([
    // PLOP_APPEND_PATTERN_ANCHOR_WATCHER
    takeEvery(CREATE_ASSET, createAssetWorker),
    takeEvery(GET_ASSETS, getAssetsWorker),
    takeEvery(CREATE_ASSETS_BATCH, createAssetsBatchWorker),
    takeEvery(CREATE_ASSET_GROUP, createAssetGroupWorker),
    takeLatest(GET_ASSET_GROUPS, getAssetGroupsWorker),
    takeEvery(GET_ASSET_GROUP, getAssetGroupWorker),
    takeEvery(UPDATE_ASSET_GROUP, updateAssetGroupWorker),
    takeEvery(APPROVE_INTERNAL_ASSET_GROUP, approveInternalAssetGroupWorker),
    takeEvery(DISAPPROVE_INTERNAL_ASSET_GROUP, disapproveInternalAssetGroupWorker),
    takeEvery(CREATE_ASSET_GROUP_SIGNAL, createAssetGroupSignalWorker)
  ])
}

// PLOP_APPEND_PATTERN_ANCHOR_WORKER
function* createAssetWorker({ assetData, isFileUploading, platform }: CreateAssetPayload): Generator {
  try {
    const createdAdAssetData: SagaReturnType<typeof createAssetService> = yield call(
      createAssetService,
      assetData,
      platform
    )

    yield put(createAssetSuccess(createdAdAssetData))

    if (isFileUploading) {
      // for google, we need to put minor delay while image is being processed
      if (platform === GOOGLE_PLATFORM) {
        yield delay(1000)
      }

      // google ads responds with new image name, so should be used name from payload i.e. assetData.name
      yield put(fileUploadToPlatformSuccess(createdAdAssetData, assetData.name))
    }
  } catch (error: any) {
    yield put(createAssetFailure(error))
    if (isFileUploading) {
      yield put(fileUploadToPlatformFailure(error, assetData.name))
      showToasts({
        type: TOAST_TYPE.error,
        message:
          'Sorry, there was an error uploading that file, please try again. If you keep experiencing errors please check the file or contact support.'
      })
    }
  }
}

function* getAssetsWorker({ params }: GetAssetsPayload): Generator {
  try {
    const { response }: SagaReturnType<typeof getAssetsService> = yield race({
      response: call(getAssetsService, params, GOOGLE_PLATFORM),
      // need to cancel previous task, if user change the page, but task still in progress
      cancel: take(CLEAR_ASSETS)
    })

    if (response) {
      yield put(getAssetsSuccess(response))
    }
  } catch (error: any) {
    yield put(getAssetsFailure(error))
  }
}

function* createAssetsBatchWorker({ assetsData }: CreateAssetsBatchPayload): Generator {
  try {
    const createdAdAssetData: SagaReturnType<typeof createAssetsBatchService> = yield call(
      createAssetsBatchService,
      assetsData,
      GOOGLE_PLATFORM
    )

    yield put(createAssetsBatchSuccess(createdAdAssetData))
  } catch (error: any) {
    yield put(createAssetsBatchFailure(error))
  }
}

function* createAssetGroupWorker({ assetGroupData }: CreateAssetGroupPayload): Generator {
  try {
    type CreatAdAssetGroupDataType = SagaReturnType<typeof createAssetGroupService>

    const creatAdAssetGroupData: CreatAdAssetGroupDataType = yield call(
      createAssetGroupService,
      assetGroupData,
      GOOGLE_PLATFORM
    )

    yield put(createAssetGroupSuccess(creatAdAssetGroupData))
  } catch (error: any) {
    yield put(createAssetGroupFailure(error))
  }
}

function* getAssetGroupsWorker({ params }: GetAssetGroupsPayload): Generator {
  try {
    const { response }: SagaReturnType<typeof getAssetGroupsService> = yield race({
      response: call(getAssetGroupsService, params, GOOGLE_PLATFORM),
      // need to cancel previous task, if user change the page, but task still in progress
      cancel: take(CLEAR_ASSET_GROUPS)
    })

    if (response) {
      yield put(getAssetGroupsSuccess(response))
    }
  } catch (error: any) {
    yield put(getAssetGroupsFailure(error))
  }
}

function* getAssetGroupWorker({ params }: GetAssetGroupPayload): Generator {
  try {
    const assetGroupData: SagaReturnType<typeof getAssetGroupService> = yield call(
      getAssetGroupService,
      params,
      GOOGLE_PLATFORM
    )

    yield put(getAssetGroupSuccess(assetGroupData))
  } catch (error: any) {
    yield put(getAssetGroupFailure(error))
  }
}

function* updateAssetGroupWorker({ assetGroupData, id }: UpdateAssetGroupPayload): Generator {
  try {
    const response: SagaReturnType<typeof updateAssetGroupService> = yield call(
      updateAssetGroupService,
      assetGroupData,
      id,
      GOOGLE_PLATFORM
    )

    yield put(updateAssetGroupSuccess(response))
  } catch (error: any) {
    if (error.errors?.asset_group_asset_operation) {
      showToasts({
        type: TOAST_TYPE.error,
        message: error.errors?.asset_group_asset_operation
      })
    }

    yield put(updateAssetGroupFailure(error))
  }
}

function* approveInternalAssetGroupWorker({ id }: ApproveInternalAssetGroupPayload): Generator {
  try {
    const response: SagaReturnType<typeof approveInternalAssetGroupService> = yield call(
      approveInternalAssetGroupService,
      id
    )

    const modifiedResponse = {
      ...response,
      // set approval to null, because BE doesn't modify this field after approval
      approval_status: null
    }

    yield put(approveInternalAssetGroupSuccess(modifiedResponse, id))

    showToasts({
      type: TOAST_TYPE.success,
      message: 'Asset group approved successfully'
    })
  } catch (error: any) {
    const errorMessage = Object.values(error.errors)?.[0]

    errorMessage &&
      showToasts({
        type: TOAST_TYPE.error,
        message: typeof errorMessage === 'string' ? errorMessage : ''
      })

    yield put(approveInternalAssetGroupFailure(error, id))
  }
}

function* disapproveInternalAssetGroupWorker({ id, data }: DisapproveInternalAssetGroupPayload): Generator {
  try {
    yield call(disapproveInternalAssetGroupService, id, data)

    yield put(disapproveInternalAssetGroupSuccess(id))
  } catch (error: any) {
    yield put(disapproveInternalAssetGroupFailure(error, id))
  }
}

function* createAssetGroupSignalWorker({ assetGroupSignalData }: CreateAssetGroupSignalPayload): Generator {
  try {
    const response: SagaReturnType<typeof createAssetGroupSignalService> = yield call(
      createAssetGroupSignalService,
      assetGroupSignalData,
      GOOGLE_PLATFORM
    )

    yield put(createAssetGroupSignalSuccess(response))
  } catch (error: any) {
    yield put(createAssetGroupSignalFailure(error))
  }
}

export default assetsWatcher
